﻿import { createElement, onEvent } from "./_dom";
import { computePosition, autoUpdate, offset, flip, shift, size, detectOverflow } from '@floating-ui/dom';

const openMenuKeys = ["Enter", "ArrowDown", "Down"];
const closeMenuKeys = ["Esc", "Escape"];

// TODO: optimize
function handleMenuKeys(event, currentItem) {
	let dir = 0;
	switch (event.key) {
		case "ArrowUp":
			dir = -2;
		// fall through
		case "ArrowDown": {
			dir++;
			const menu = event.target.closest("[role=menu]");
			if (!menu)
				break;
			const items = Array.from(menu.querySelectorAll("[role=menuitem]").values()).filter(item => item.closest("[role=menu]") === menu);
			if (items.length > 1) {
				const currentIndex = items.indexOf(currentItem);
				if (currentIndex >= 0) {
					let newIndex = currentIndex + dir;
					if (newIndex >= items.length)
						newIndex = 0;
					else if (newIndex < 0)
						newIndex = items.length - 1;
					const newItem = items[newIndex];
					currentItem.setAttribute("tabindex", -1);
					newItem.removeAttribute("tabindex");
					newItem.focus();
				}
			}
			break;
		}
		case "ArrowRight":
			// const thisItem = event.target.closest("[role=menuitem]");
			// if (thisItem && thisItem.matches("[aria-haspopup=true]"))
			break;
		case "Esc":
		case "Escape": {
			const popup = currentItem.closest(".v3-popup");
			if (popup != null) {
				closePopup(popup);
			}
			break;
		}
	}
}

const kPopupSelector = ".js-auto-popup[aria-haspopup=true]";

function initPopup(toggler, popup) {
	if(!popup || popup.popperInstance)
		return;
	const likelySubmenu = toggler.matches(".v3-menu__item") && popup.matches(".v3-menu");
	const [primaryPlacement, ...placements] = (toggler.dataset.popperPlacement || (likelySubmenu ? "right-start" : "bottom-start")).split(/\s+/);
	const detectOverflowOptions = { padding: { top: 5, right: 5, bottom: 21, left: 5 } };
	const middleware = [offset(likelySubmenu ? 0 : 2)];
	if (popup.dataset.popupAutoheight != null || popup.classList.contains("v3-menu-scrollable")) {
		middleware.push(size({
			...detectOverflowOptions,
			async apply(state) {
				const overflow = await detectOverflow(state, detectOverflowOptions);
				const viewportHeight = Math.max(0, state.rects.floating.height - overflow.top - overflow.bottom);
				// Shrink to the preferred height before flipping; smaller only if the viewport requires it.
				popup.style.maxHeight = `min(${viewportHeight}px, max(var(--v3-popup-preferred-height, 300px), ${Math.max(0, state.availableHeight)}px))`;
			}
		}));
	}
	middleware.push(
		flip({
			...detectOverflowOptions,
			fallbackPlacements: placements.length ? placements : undefined,
			crossAxis: "alignment",
			fallbackAxisSideDirection: "end",
			fallbackStrategy: "initialPlacement"
		}),
		// Overlap the trigger only after the alternative placements have been tried.
		shift({ ...detectOverflowOptions, crossAxis: true })
	);
	const popperCleanup = autoUpdate(toggler, popup, () => computePosition(toggler, popup, {
		placement: primaryPlacement,
		middleware
	}).then(({ x, y }) => { popup.style.left = `${x}px`; popup.style.top = `${y}px`; }));

	if(popup.getAttribute("tabindex"))
		popup.focus();

	popup["popperCleanup"] = popperCleanup;
}

// eslint-disable-next-line no-unused-vars
function initPopups(context, selector = kPopupSelector) {
	const togglers = context.querySelectorAll(selector);
	togglers.forEach(toggler => {
		const ariaControls = toggler.getAttribute("aria-controls");
		const popup = ariaControls && document.getElementById(ariaControls);
		if (popup) {
			initPopup(toggler, popup);
		}
	});
}

function togglePopup(e, toggler) {
	const ariaControls = toggler.getAttribute("aria-controls");
	const popup = ariaControls && document.getElementById(ariaControls);
	if (popup) {
		const expanded = toggler.getAttribute("aria-expanded") == "true";
		let action = "";
		if (e.type === "click" || e.type === "keydown" && e.key === " ") {
			action = expanded ? "close" : "open";
			// suppress close if clicked inside and option is set
			if (popup.dataset.noAutoClose && e.type === "click" && action === "close" && popup.contains(e.target))
				action = "";
		} else if (e.type === "keydown" && popup.getAttribute("role") === "menu") {
			if (openMenuKeys.indexOf(e.key) >= 0) {
				action = expanded ? "" : "open";
			}
			else if (closeMenuKeys.indexOf(e.key) >= 0) {
				action = expanded ? "close" : "";
			}
		}
		if (action === "close") {
			toggler.removeAttribute("aria-expanded");
			if (popup.contains(document.activeElement))
				toggler.focus();
			popup.classList.remove("show");
			toggler.dispatchEvent(new CustomEvent("v3-popup.close", { detail: popup }));
		} else if (action === "open") {
			toggler.setAttribute("aria-expanded", "true");
			initPopup(toggler, popup);
			toggler.dispatchEvent(new CustomEvent("v3-popup.beforeopen", { detail: popup }));
			popup.classList.add("show");
			toggler.dispatchEvent(new CustomEvent("v3-popup.open", { detail: popup }));
			popup.popperInstance?.update();
			if (popup.tabIndex === -1)
				popup.focus({ preventScroll: true });
			e.preventDefault();
		}
	}
}

function closePopup(popup, context) {
	var toggler = (context || document).querySelector(`[aria-controls="${popup.id}"]`);
	if (toggler) {
		toggler.removeAttribute("aria-expanded");
		if (popup.contains(document.activeElement))
			toggler.focus();
		popup.classList.remove("show");
		toggler.dispatchEvent(new CustomEvent("v3-popup.close", { detail: popup }));
	}
}

function closePopups({ target, currentTarget: context }) {
	const targetPopup = target.closest(".v3-popup");
	(targetPopup || context).querySelectorAll(".v3-popup.show").forEach(e => {
		var toggler = (context || document).querySelector(`[aria-controls="${e.id}"]`);
		if (toggler && !toggler.contains(target)) {
			toggler.removeAttribute("aria-expanded");
			e.classList.remove("show");
			toggler.dispatchEvent(new CustomEvent("v3-popup.close", { detail: e }));
		}
	});
}

function initPopupContainer(context) {
	let container = document.getElementById("v3-popup-container");
	if (!container) {
		container = createElement("div", { id: "v3-popup-container", class: "v3-popup-container td-embedded" });
		(context || document.body).append(container);
	}
	return container;
}

function init(context, selector = kPopupSelector) {
	// order matters
	context.addEventListener("click", closePopups);
	onEvent(context, selector, "click keydown", togglePopup);
	onEvent(context, ".v3-menu__item", "keydown", handleMenuKeys);
	// initPopups(context, selector);
}

export { init, initPopupContainer, initPopup, closePopup, closePopups };
